"use client";

import {
  SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel,
  SiteTemplatePageSectionTypeAppointmentImagesListItemSettingModel,
} from "@/Apis/ClinicApi";
import { Icon } from "@iconify/react";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { AppointmentSectionProps } from "@/Templates/SectionsProps";
import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import HomeEightBookSectionGenerated, {
  HomeEightBookSectionGeneratedProps,
} from "../Sections/_generated/HomeEightBookSection.generated";
import HomeFifteenItSectionGenerated, {
  HomeFifteenItSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenItSection.generated";
import HomeFiveJourneySectionGenerated, {
  HomeFiveJourneySectionGeneratedProps,
} from "../Sections/_generated/HomeFiveJourneySection.generated";
import HomeNineJourneySectionGenerated, {
  HomeNineJourneySectionGeneratedProps,
} from "../Sections/_generated/HomeNineJourneySection.generated";
import HomeOneBookusSectionGenerated, {
  HomeOneBookusSectionGeneratedProps,
} from "../Sections/_generated/HomeOneBookusSection.generated";
import HomeSevenAppointmentSectionGenerated, {
  HomeSevenAppointmentSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenAppointmentSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedAppointmentRenderer = (
  props: AppointmentSectionProps,
) => ReactElement;

const AppointmentAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateAppointmentAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getListItems(
  props: AppointmentSectionProps,
): SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel[] {
  return props.lists.BookusSteps;
}

function getImageItems(
  props: AppointmentSectionProps,
): SiteTemplatePageSectionTypeAppointmentImagesListItemSettingModel[] {
  return props.lists.Images ?? [];
}

function getSectionImageUrl(
  props: AppointmentSectionProps,
): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: AppointmentSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function isImageLikeValue(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function getItemImageUrl(
  item?:
    | SiteTemplatePageSectionTypeAppointmentImagesListItemSettingModel
    | SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel
    | null,
): string | undefined {
  return item && "Image" in item ? item.Image?.Url : undefined;
}

function renderIconifyNode(node: ReactNode, iconName: string): ReactNode {
  if (!isValidElement(node)) {
    return node;
  }

  if (node.type === "i") {
    return <Icon icon={iconName} />;
  }

  const children = (node.props as { children?: ReactNode }).children;
  if (children === undefined) {
    return node;
  }

  return cloneElement(
    node,
    undefined,
    Children.map(children, (child) => renderIconifyNode(child, iconName)),
  );
}

function getItemImageAlt(
  item:
    | SiteTemplatePageSectionTypeAppointmentImagesListItemSettingModel
    | SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel
    | undefined,
  fallback: string,
): string {
  const title = item && "Title" in item ? item.Title : undefined;
  return (item && "Image" in item ? item.Image?.AltText : undefined) ?? title ?? fallback;
}

function getItemVisual(
  item: SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel | undefined,
  fallback?: string,
): string | undefined {
  if (isImageLikeValue(item?.Icon)) {
    return item?.Icon ?? undefined;
  }

  return getItemImageUrl(item) ?? fallback;
}

function getItemIconClass(
  item: SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel | undefined,
  fallback: string,
): string {
  if (item?.Icon && !isImageLikeValue(item.Icon)) {
    return item.Icon;
  }

  return fallback;
}

function getItemStepText(
  item: SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel | undefined,
  index: number,
): string {
  return item?.Subtitle || `STEP ${index + 1}`;
}

function getItemPaddedNumber(index: number): string {
  return String(index + 1).padStart(2, "0");
}

function getHomeSevenTitle(
  item: SiteTemplatePageSectionTypeAppointmentBookusStepsListItemSettingModel,
  index: number,
): string {
  const step = item.Subtitle || getItemPaddedNumber(index);
  const cleanStep = `${step}`.trim();
  return cleanStep
    ? `${cleanStep}. ${item.Title ?? ""}`.trim()
    : (item.Title ?? "");
}

const generatedAppointmentRenderers: Record<
  string,
  GeneratedAppointmentRenderer
> = {
  [AppointmentAppearance.Home1.key]: (props) => (
    <HomeOneBookusSectionGenerated
      {...buildGeneratedSectionProps<HomeOneBookusSectionGeneratedProps>(
        "HomeOneBookusSection",
        {
          sectionDiv0Div0Div1Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div1Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div1Div0H20H2Text2: "",
          sectionDiv0Div0Div1Div0H20Span0SpanText: "",
          sectionDiv0Div0Div1Div0H20Span0SpanText2: "",
          sectionDiv0Div0Div1Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          bookusSectionAccordions: (props.lists.Default ?? []).map(
            (item, index) => ({
              key: item.Id,
              className:
                index === 0
                  ? "accordion-collapse collapse show"
                  : "accordion-collapse collapse",
              id: index === 0 ? "collapseSix" : "collapseSeven",
              h30Id: index === 0 ? "headingTen" : "headingEleven",
              link0Text: item.Title ?? "",
              ariaControls: index === 0 ? "collapseSix" : "collapseSeven",
              ariaExpanded: index === 0,
              link0ClassName:
                index === 0 ? "accordion-button" : "accordion-button collapsed",
              dataBsTarget: index === 0 ? "#collapseSix" : "#collapseSeven",
              dataBsParent: "#faq-details",
              p0Text: renderHtmlValue(item.Description),
              href: "/",
            }),
          ),
          bookusSection12ZoomInUps: getImageItems(props)
            .slice(0, 3)
            .map((item, index) => ({
              key: item.Id,
              className:
                index === 0
                  ? "col-md-12 wow zoomInUp"
                  : "col-sm-6 wow zoomInUp",
              src:
                getItemImageUrl(item) ??
                `/Templates/Doccure/img/book-0${index + 1}.jpg`,
              alt: getItemImageAlt(item, `appointment-${index + 1}`),
              dataWowDuration: `${index + 1}s`,
            })),
          bookusSection36s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className:
              [
                "book-icon bg-primary",
                "book-icon bg-orange",
                "book-icon bg-cyan",
                "book-icon bg-primary",
              ][index] ?? "book-icon bg-primary",
            i0ClassName: item.Icon ?? getItemIconClass(item, "isax isax-calendar5"),
            h30Text: item.Title ?? "",
            p0Text: renderHtmlValue(item.Description),
            h40Text: item.Subtitle ?? getItemPaddedNumber(index),
            dataWowDuration: `${index + 1}s`,
          })),
        },
      )}
      bookusSection36sItemWrapper={(item, content) =>
        item.i0ClassName?.includes(":") && !isImageLikeValue(item.i0ClassName)
          ? renderIconifyNode(content, item.i0ClassName)
          : content
      }
      sectionDiv0Div0Div1Div0H20H2Renderer={(defaultNode) => {
        if (!props.setting.Title) {
          return defaultNode;
        }

        return <h2 className="section-title">{props.setting.Title}</h2>;
      }}
    />
  ),
  [AppointmentAppearance.Home5.key]: (props) => (
    <HomeFiveJourneySectionGenerated
      {...buildGeneratedSectionProps<HomeFiveJourneySectionGeneratedProps>(
        "HomeFiveJourneySection",
        {
          sectionDiv0Div0Div0Div0Div0ImageWithBasePath0ImageVisible: false,
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div1Div0Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div1Div0Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div1Div0Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "journey",
          ),
          journeySectionJourneyFives: getListItems(props).map(
            (item, index) => ({
              key: item.Id,
              div0Text: item.Subtitle ?? getItemPaddedNumber(index),
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              className:
                index === getListItems(props).length - 1
                  ? "journey-item-five mb-0"
                  : "journey-item-five",
            }),
          ),
        },
      )}
    />
  ),
  [AppointmentAppearance.Home7.key]: (props) => (
    <HomeSevenAppointmentSectionGenerated
      {...buildGeneratedSectionProps<HomeSevenAppointmentSectionGeneratedProps>(
        "HomeSevenAppointmentSection",
        {
          sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0H20Span0SpanText:
            props.setting.Subtitle ?? undefined,
          appointmentSection36s: getListItems(props).map((item, index) => ({
            key: item.Id,
            h30Text: getHomeSevenTitle(item, index),
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [AppointmentAppearance.Home8.key]: (props) => (
    <HomeEightBookSectionGenerated
      {...buildGeneratedSectionProps<HomeEightBookSectionGeneratedProps>(
        "HomeEightBookSection",
        {
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0H30H3Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0H30H3Text2: "",
          sectionDiv0Div0Div0Div0H30Span0SpanText:
            props.setting.Description ?? undefined,
          sectionDiv0Div0Div1ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div1ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "appointment",
          ),
          bookSection3466s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className: getItemIconClass(item, "isax isax-calendar-tick5"),
            h30Text: item.Title ?? "",
            h40Text: item.Subtitle ?? getItemPaddedNumber(index),
            p0Text: renderHtmlValue(item.Description),
          })),
        },
      )}
    />
  ),
  [AppointmentAppearance.Home9.key]: (props) => (
    <HomeNineJourneySectionGenerated
      {...buildGeneratedSectionProps<HomeNineJourneySectionGeneratedProps>(
        "HomeNineJourneySection",
        {
          journeySection46s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className:
              [
                "journey-item-nine one wow fadeInUp",
                "journey-item-nine two wow fadeInUp",
                "journey-item-nine three wow fadeInUp",
              ][index] ?? "journey-item-nine one wow fadeInUp",
            dataWowDuration: `${index + 1}s`,
            h40Text: item.Title ?? "",
            href: "#",
            src:
              getItemVisual(
                item,
                "/Templates/Doccure/img/about/commit-bg-1.png",
              ) ?? "/Templates/Doccure/img/about/commit-bg-1.png",
            imageWithBasePath1Src:
              getItemImageUrl(item) ??
              getSectionImageUrl(props) ??
              "/Templates/Doccure/img/about/commit-img-1.png",
            alt: getItemImageAlt(item, "appointment"),
            imageWithBasePath1Alt: getItemImageAlt(item, "appointment"),
          })),
        },
      )}
    />
  ),
  [AppointmentAppearance.Home15.key]: (props) => (
    <HomeFifteenItSectionGenerated
      {...buildGeneratedSectionProps<HomeFifteenItSectionGeneratedProps>(
        "HomeFifteenItSection",
        {
          sectionDiv0Div0Div0Div0Div0DivText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0H20Span0SpanText:
            props.setting.Description ?? undefined,
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div1Div0ImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "appointment",
          ),
          itSectionHowFifteenFadeInUps: getListItems(props).map(
            (item, index) => ({
              key: item.Id,
              span0Text: getItemStepText(item, index),
              h30Text: item.Title ?? "",
              p0Text: renderHtmlValue(item.Description),
              src:
                getItemVisual(
                  item,
                  "/Templates/Doccure/img/icons/how-icon-1.svg",
                ) ?? "/Templates/Doccure/img/icons/how-icon-1.svg",
              alt: getItemImageAlt(item, "icon"),
              dataWowDuration: `${index + 2}s`,
            }),
          ),
        },
      )}
    />
  ),
};

const GeneratedAppointmentSection: FC<AppointmentSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedAppointmentRenderers,
    AppointmentAppearance.Home1.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedAppointmentSection;
